import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { endpoints, getAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { Wordmark, useBrand } from "@/context/BrandContext";

export function VerifyResult({ result }) {
  if (!result) return null;
  const ok = result.valid && result.run_unchanged;
  const Icon = !result.valid ? ShieldAlert : ok ? ShieldCheck : ShieldQuestion;
  const tone = !result.valid ? "border-rose-500/40 text-rose-600" : ok ? "border-emerald-500/40 text-emerald-700" : "border-amber-500/40 text-amber-700";
  return (
    <div data-testid="verify-result" className={`border p-5 ${tone} bg-zinc-50`}>
      <div className="flex items-center gap-2 mb-3 font-heading font-medium">
        <Icon className="w-5 h-5" />
        {!result.valid ? "Seal invalid" : ok ? "Seal valid · run unchanged" : "Seal valid · run modified after sealing"}
      </div>
      <div className="grid grid-cols-2 gap-y-2 text-[12px] font-mono text-zinc-700">
        <span className="text-zinc-500">reason</span><span data-testid="verify-reason">{result.reason || "—"}</span>
        <span className="text-zinc-500">seal</span><span>{result.seal_id || "—"}</span>
        <span className="text-zinc-500">run</span><span>{result.run_id || "—"}</span>
        <span className="text-zinc-500">issued</span><span>{result.issued_at || "—"}</span>
        <span className="text-zinc-500">issued by</span><span>{result.issued_by || "—"}</span>
        <span className="text-zinc-500">events sealed</span><span>{result.event_count ?? "—"}</span>
        <span className="text-zinc-500">run status</span><span>{result.run_status || "—"}</span>
      </div>
    </div>
  );
}

export function VerifyForm({ initialSeal = "", initialSig = "", onResult }) {
  const [seal, setSeal] = useState(initialSeal);
  const [sig, setSig] = useState(initialSig);
  const [result, setResult] = useState(null);

  const run = async (s = seal, g = sig) => {
    if (!s || !g) return;
    const r = await endpoints.verifySeal(s, g);
    setResult(r);
    onResult?.(r);
  };

  useEffect(() => {
    if (initialSeal && initialSig) run(initialSeal, initialSig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSeal, initialSig]);

  const cls = "bg-white border-zinc-200 rounded-sm h-9 font-mono text-[12px] focus-visible:ring-orange-500/40";
  return (
    <div className="space-y-3">
      <Input data-testid="verify-seal-input" placeholder="seal-xxxxxxxxxx" value={seal} onChange={(e) => setSeal(e.target.value)} className={cls} />
      <Input data-testid="verify-signature-input" placeholder="HMAC-SHA256 signature (64 hex chars)" value={sig} onChange={(e) => setSig(e.target.value)} className={cls} />
      <Button data-testid="verify-button" onClick={() => run()} disabled={!seal || !sig}
              className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm h-9 gap-2">
        <ShieldCheck className="w-3.5 h-3.5" /> Verify seal
      </Button>
      <VerifyResult result={result} />
    </div>
  );
}

export default function Verify() {
  const [params] = useSearchParams();
  const brand = useBrand();
  return (
    <div className="min-h-screen bg-white text-zinc-900 flex items-center justify-center p-8 tx-grid-bg" data-testid="page-verify">
      <div className="w-full max-w-xl border border-zinc-200 bg-white p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <Wordmark />
          <span className="tx-overline">audit verifier</span>
        </div>
        <h1 className="font-heading font-black text-3xl mb-2">Verify a signed trace.</h1>
        <p className="text-zinc-600 text-[13px] mb-6">
          Paste the seal ID and HMAC signature printed at the bottom of a {brand.app_name} trace PDF.
          Verification is public and does not reveal trace contents.
        </p>
        <VerifyForm initialSeal={params.get("seal") || ""} initialSig={params.get("sig") || ""} />
        <div className="mt-8 text-[11px] font-mono text-zinc-400">
          {getAuthToken() ? <Link to="/audit" className="hover:text-zinc-700">← back to audit log</Link> : <Link to="/login" className="hover:text-zinc-700">operator sign in →</Link>}
        </div>
      </div>
    </div>
  );
}
